import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from '@chakra-ui/react';
import { BsTrash } from 'react-icons/bs';
import { ReactSVG } from 'react-svg';
import rightArrow from '../../../assets/images/icons/right-arrow.svg';
import { Drawer } from '../../../components/UI-CHAKRA/Drawer';
import { DrawerOverlay } from '../../../components/UI-CHAKRA/DrawerOverlay';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Spinner } from '../../../components/UI-CHAKRA/Spinner';
import { Input } from '../../../components/UI-CHAKRA/Input/Input';
import { Switch } from '../../../components/UI-CHAKRA/Switch';
import { HospitalBuildingPinIcon } from './icons';
import { Button } from '../../../components/UI-CHAKRA/Button';
import { IconButton } from '../../../components/UI-CHAKRA/IconButton';
import { Tracker } from '../../../shared/tracker/Tracker';
import {
  Model,
  ModelDetails,
  ModelService,
  ProtocolOrigin,
  UpdateProtocolExamItemInput,
  UpdateProtocolPayload,
} from '../services';
import { FormulaService } from '../../../components/search/formulaService';
import { activeModelSearchName, useModelStore } from '../../../stores/modelStore';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { useLoading } from '../../../helpers/useLoading';
import { Yup } from '../../../helpers/Yup';
import { toast } from '../../../helpers/toast';
import { extractPatientAge } from '../../../helpers/patientAge';
import { logger } from '../../../helpers/logger';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';

const EditModelSchema = Yup.object().shape({
  modelName: Yup.string().trim().required('Informe um nome para o modelo'),
});

interface EditModelDrawerProps {
  isOpen: boolean;
  model: Model | null;
  onClose: () => void;
}

type RemovableItemKind =
  | 'medication'
  | 'exam'
  | 'certificate'
  | 'referral'
  | 'report'
  | 'compoundedFormula';

interface ItemPreview {
  key: string;
  primary: string;
  secondary?: string;
  kind: RemovableItemKind;
  itemId: string;
  examGroupId?: string;
}

function buildRemovalPayload(items: ItemPreview[]): Partial<UpdateProtocolPayload> {
  if (items.length === 0) return {};

  const payload: Partial<UpdateProtocolPayload> = {};
  const examGroups = new Map<string, UpdateProtocolExamItemInput[]>();

  for (const item of items) {
    const destroy = { id: item.itemId, _destroy: true } as const;
    switch (item.kind) {
      case 'medication':
        payload.medications = [...(payload.medications ?? []), destroy];
        break;
      case 'certificate':
        payload.certificates = [...(payload.certificates ?? []), destroy];
        break;
      case 'referral':
        payload.referrals = [...(payload.referrals ?? []), destroy];
        break;
      case 'report':
        payload.reports = [...(payload.reports ?? []), destroy];
        break;
      case 'compoundedFormula':
        payload.compoundedFormulas = [...(payload.compoundedFormulas ?? []), destroy];
        break;
      case 'exam': {
        if (!item.examGroupId) break;
        examGroups.set(item.examGroupId, [...(examGroups.get(item.examGroupId) ?? []), destroy]);
        break;
      }
    }
  }

  if (examGroups.size > 0) {
    payload.exams = Array.from(examGroups, ([id, items_]) => ({ id, items: items_ }));
  }

  return payload;
}

export function EditModelDrawer(props: EditModelDrawerProps) {
  const { isOpen, model, onClose } = props;

  const prescription = usePrescriptionStore((store) => store.prescription);
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);
  const fetchModels = useModelStore((store) => store.fetchModels);
  const setFeedback = useModelStore((store) => store.setFeedback);

  const [details, setDetails] = useState<ModelDetails | null>(null);
  const [formulaNames, setFormulaNames] = useState<Record<number, string>>({});
  const [pendingRemovals, setPendingRemovals] = useState<ItemPreview[]>([]);
  const [selectedOrigin, setSelectedOrigin] = useState<ProtocolOrigin | null>(null);
  const [loadingDetails, runWithLoadingDetails] = useLoading();
  const [saving, runWithSaving] = useLoading();
  const isMobile = useIsMobile();

  const prescriptionId = prescription?.idPrescricao;
  const doctorId = prescription?.Prescritor?.idPrescritor;
  const partnerId = prescriberConfiguration?.idParceiro;
  const modelId = model?.id;
  const loadDetails = useCallback(async () => {
    if (!modelId || !prescriptionId) return;
    runWithLoadingDetails(async () => {
      try {
        const fetched = await ModelService.fetchModelDetails(modelId, doctorId ?? 0, partnerId ?? 0, prescriptionId);
        setDetails(fetched);
      } catch (e) {
        logger.error('Error fetching model details for edit:', e);
        toast.error('Não foi possível carregar o modelo, tente novamente.');
      }
    });
  }, [modelId, prescriptionId, doctorId, partnerId, runWithLoadingDetails]);

  useEffect(() => {
    if (!isOpen) {
      setDetails(null);
      setPendingRemovals([]);
      setSelectedOrigin(null);
      return;
    }
    loadDetails();
  }, [isOpen, loadDetails]);

  useEffect(() => {
    if (!details?.compoundedFormulas?.length) {
      setFormulaNames({});
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        details.compoundedFormulas.map(async (formula) => {
          try {
            const lib = await FormulaService.getDoctorFormulaById(details.doctorId, formula.formulaId);
            return [formula.formulaId, lib.formulaName ?? ''] as const;
          } catch {
            return [formula.formulaId, ''] as const;
          }
        })
      );
      if (!cancelled) {
        setFormulaNames(Object.fromEntries(entries.filter(([, name]) => name)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [details]);

  const medicineItems: ItemPreview[] = useMemo(() => {
    if (!details) return [];
    return (details.medications ?? []).map((item) => ({
      key: `med-${item.id}`,
      primary: item.brandName ?? item.activeIngredient ?? '',
      secondary: item.posology ?? '',
      kind: 'medication',
      itemId: item.id,
    }));
  }, [details]);

  const examItems: ItemPreview[] = useMemo(() => {
    if (!details) return [];
    return (details.exams ?? []).flatMap((group) =>
      (group.items ?? []).map<ItemPreview>((item) => ({
        key: `exam-${item.id}`,
        primary: item.description ?? '',
        kind: 'exam' as const,
        itemId: item.id,
        examGroupId: group.id,
      }))
    );
  }, [details]);

  const certificateItems: ItemPreview[] = useMemo(() => {
    if (!details) return [];
    return (details.certificates ?? []).map((item) => ({
      key: `cert-${item.id}`,
      primary: item.reason ?? 'Atestado',
      secondary: item.observation ?? '',
      kind: 'certificate' as const,
      itemId: item.id,
    }));
  }, [details]);

  const referralItems: ItemPreview[] = useMemo(() => {
    if (!details) return [];
    return (details.referrals ?? []).map((item) => ({
      key: `ref-${item.id}`,
      primary: item.specialty ?? 'Encaminhamento',
      secondary: item.reason ?? '',
      kind: 'referral' as const,
      itemId: item.id,
    }));
  }, [details]);

  const reportItems: ItemPreview[] = useMemo(() => {
    if (!details) return [];
    return (details.reports ?? []).map((item) => ({
      key: `report-${item.id}`,
      primary: item.documentType ?? 'Relatório',
      secondary: item.description ?? '',
      kind: 'report' as const,
      itemId: item.id,
    }));
  }, [details]);

  const compoundedFormulaItems: ItemPreview[] = useMemo(() => {
    if (!details) return [];
    return (details.compoundedFormulas ?? []).map((item) => ({
      key: `formula-${item.id}`,
      primary: formulaNames[item.formulaId] ?? 'Fórmula',
      kind: 'compoundedFormula' as const,
      itemId: item.id,
    }));
  }, [details, formulaNames]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    initialValues: {
      modelName: model?.name ?? '',
    },
    validationSchema: EditModelSchema,
    onSubmit: (values) => {
      if (!model) return;
      const trimmed = values.modelName.trim();

      runWithSaving(async () => {
        const partnerId = prescriberConfiguration?.idParceiro;
        if (!prescription || !partnerId) {
          logger.error('EditModelDrawer: dados do prescritor indisponíveis para salvar o modelo', {
            hasPrescription: Boolean(prescription),
            hasPartnerId: Boolean(partnerId),
          });
          toast.error('Não foi possível salvar o modelo. Recarregue a página e tente novamente.');
          return;
        }

        const renamed = trimmed !== model.name;
        const hasRemovals = pendingRemovals.length > 0;
        // A origem salva é a do toggle; sem toggle, a que o modelo já tinha.
        const nextOrigin: ProtocolOrigin = canChangeOrigin && targetOrigin ? targetOrigin : model.origin;
        const originChanged = nextOrigin !== model.origin;

        try {
          // O homônimo é checado na lista de DESTINO: pessoal e institucional podem coexistir
          // com o mesmo nome, então um nome único entre os modelos do médico pode colidir
          // entre os da clínica, e vice-versa.
          if (renamed || originChanged) {
            const duplicated = await ModelService.findModelByName(
              prescription.Prescritor.idPrescritor,
              prescription.idPrescricao,
              partnerId,
              trimmed,
              nextOrigin,
              model.id,
            );
            if (duplicated) {
              formik.setFieldError(
                'modelName',
                originChanged
                  ? `Já existe um modelo ${nextOrigin === 'institutional' ? 'institucional' : 'pessoal'} com esse nome`
                  : 'Não é possível salvar modelos com o mesmo nome',
              );
              return;
            }
          }

          if (renamed || hasRemovals || originChanged) {
            await ModelService.updateModel(model.id, {
              doctorId: prescription.Prescritor.idPrescritor,
              partnerId,
              prescriptionId: prescription.idPrescricao,
              ...(renamed && { name: trimmed }),
              ...(originChanged && { origin: nextOrigin }),
              ...buildRemovalPayload(pendingRemovals),
            });
          }

          Tracker.trackEvent({
            event: 'Model Edited',
            parameters: { editedModel: trimmed },
          });

          await fetchModels(
            prescription.Prescritor.idPrescritor,
            prescription.idPrescricao,
            extractPatientAge(prescription.Paciente?.Idade),
            activeModelSearchName(),
            { force: true },
          );

          setFeedback({ type: 'saved', modelName: trimmed });
          onClose();
        } catch (e) {
          logger.error('Error updating model:', e);
          toast.error('Não foi possível salvar o modelo, tente novamente.');
        }
      });
    },
  });

  const removeItem = useCallback((item: ItemPreview) => {
    setPendingRemovals((prev) => (prev.some((p) => p.key === item.key) ? prev : [...prev, item]));
  }, []);

  const undoRemoveItem = useCallback((item: ItemPreview) => {
    setPendingRemovals((prev) => prev.filter((p) => p.key !== item.key));
  }, []);

  const handleClose = () => {
    if (saving) return;
    formik.resetForm();
    onClose();
  };

  const isInvalid = Boolean(formik.errors.modelName) && formik.touched.modelName;
  // A origem não é editável (é definida na criação), mas precisa ficar visível: o drawer de
  // edição era a única tela do fluxo que não distinguia modelo institucional de pessoal, e
  // renomear ou remover item de um institucional afeta todos os prescritores do parceiro.
  const isInstitutionalModelAdmin = useModelStore((store) => store.isInstitutionalModelAdmin);
  // Só administrador troca a origem, nos dois sentidos. Patrocinado não é editável (o menu
  // de ações nem aparece), mas o guard fica explícito.
  const canChangeOrigin = isInstitutionalModelAdmin && model?.origin !== 'sponsored';
  // `selectedOrigin` só existe depois que o prescritor mexe no toggle; antes disso vale a
  // origem atual do modelo. Assim o aviso e o toggle refletem o estado que será salvo.
  const targetOrigin: ProtocolOrigin | undefined = selectedOrigin ?? model?.origin;
  const isInstitutionalTarget = targetOrigin === 'institutional';
  const isPendingRemoval = useCallback(
    (key: string) => pendingRemovals.some((item) => item.key === key),
    [pendingRemovals]
  );
  const hasItems =
    medicineItems.length > 0 ||
    examItems.length > 0 ||
    certificateItems.length > 0 ||
    referralItems.length > 0 ||
    reportItems.length > 0 ||
    compoundedFormulaItems.length > 0;

  return (
    <Drawer isOpen={isOpen} placement={'right'} onClose={handleClose} size={'md'}>
      <DrawerOverlay bg={'rgba(0, 0, 0, 0.4)'} />
      <DrawerContent maxW={{ base: '100%', md: '445px' }} data-cy={'editar-modelo-drawer'}>
        <DrawerHeader
          display={'flex'}
          alignItems={'center'}
          h={'72px'}
          px={'24px'}
          py={'24px'}
          fontSize={'18px'}
          fontWeight={600}
          color={'#1E252B'}
          borderBottom={'1px solid #DEE2E6'}
        >
          Editar modelo
        </DrawerHeader>
        <DrawerCloseButton top={'24px'} right={'24px'} color={'#1E252B'} />

        <DrawerBody p={0} overflowY={'auto'}>
          <form id={'editar-modelo-form'} onSubmit={formik.handleSubmit}>
            <Box px={'16px'} py={'16px'} borderBottom={'1px solid #DEE2E6'}>
              <Input
                placeholder={'Digite o nome do modelo'}
                value={formik.values.modelName}
                onChange={(e) => formik.setFieldValue('modelName', e.target.value)}
                onBlur={() => formik.setFieldTouched('modelName', true)}
                maxLength={30}
                h={'40px'}
                fontSize={'14px'}
                borderRadius={'10px'}
                borderColor={isInvalid ? 'error.main' : '#CBBFE6'}
                data-testid={'editar-modelo-name-input'}
                autoFocus
              />
              {isInvalid && (
                <Text mt={'6px'} fontSize={'12px'} color={'error.main'}>
                  {formik.errors.modelName}
                </Text>
              )}
              {canChangeOrigin && (
                <Switch
                  id={'editar-modelo-institucional'}
                  mt={'16px'}
                  display={'flex'}
                  alignItems={'center'}
                  isChecked={isInstitutionalTarget}
                  onChange={(e) => setSelectedOrigin(e.target.checked ? 'institutional' : 'personal')}
                  data-testid={'editar-modelo-institucional'}
                >
                  <Text fontSize={'14px'} lineHeight={'1.2'} color={'#343A40'}>
                    Modelo institucional
                  </Text>
                </Switch>
              )}
              {isInstitutionalTarget && <InstitutionalNotice />}
            </Box>

            {loadingDetails && !details && (
              <Flex justifyContent={'center'} alignItems={'center'} py={'40px'}>
                <Spinner color={'primary.main'} />
              </Flex>
            )}

            {medicineItems.length > 0 && (
              <Section title={'Medicamento'}>
                {medicineItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    item={item}
                    onRemove={() => removeItem(item)}
                    onUndoRemove={() => undoRemoveItem(item)}
                    pendingRemoval={isPendingRemoval(item.key)}
                  />
                ))}
              </Section>
            )}

            {compoundedFormulaItems.length > 0 && (
              <Section title={'Fórmula'}>
                {compoundedFormulaItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    item={item}
                    onRemove={() => removeItem(item)}
                    onUndoRemove={() => undoRemoveItem(item)}
                    pendingRemoval={isPendingRemoval(item.key)}
                  />
                ))}
              </Section>
            )}

            {examItems.length > 0 && (
              <Section title={'Exames e Cirurgias'}>
                {examItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    item={item}
                    onRemove={() => removeItem(item)}
                    onUndoRemove={() => undoRemoveItem(item)}
                    pendingRemoval={isPendingRemoval(item.key)}
                  />
                ))}
              </Section>
            )}

            {certificateItems.length > 0 && (
              <Section title={'Atestado'}>
                {certificateItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    item={item}
                    onRemove={() => removeItem(item)}
                    onUndoRemove={() => undoRemoveItem(item)}
                    pendingRemoval={isPendingRemoval(item.key)}
                  />
                ))}
              </Section>
            )}

            {referralItems.length > 0 && (
              <Section title={'Encaminhamento'}>
                {referralItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    item={item}
                    onRemove={() => removeItem(item)}
                    onUndoRemove={() => undoRemoveItem(item)}
                    pendingRemoval={isPendingRemoval(item.key)}
                  />
                ))}
              </Section>
            )}

            {reportItems.length > 0 && (
              <Section title={'Relatório'}>
                {reportItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    item={item}
                    onRemove={() => removeItem(item)}
                    onUndoRemove={() => undoRemoveItem(item)}
                    pendingRemoval={isPendingRemoval(item.key)}
                  />
                ))}
              </Section>
            )}

            {!loadingDetails && !hasItems && (
              <Box px={'24px'} py={'24px'}>
                <Text fontSize={'14px'} color={'#868E96'}>
                  Esse modelo não possui itens.
                </Text>
              </Box>
            )}
          </form>
        </DrawerBody>

        <DrawerFooter
          borderTop={'1px solid #DEE2E6'}
          gap={{ base: '12px', md: '16px' }}
          justifyContent={{ base: 'space-between', md: 'flex-end' }}
          py={'20px'}
        >
          <Button
            variant={'outline'}
            borderRadius={'20px'}
            borderColor={'primary.main'}
            color={'primary.main'}
            minW={'100px'}
            flex={{ base: 1, md: 'unset' }}
            h={'40px'}
            onClick={handleClose}
            isDisabled={saving}
          >
            {isMobile ? 'Voltar' : 'Cancelar'}
          </Button>
          <Button
            type={'submit'}
            form={'editar-modelo-form'}
            variant={'solid'}
            borderRadius={'20px'}
            minW={'120px'}
            flex={{ base: 1, md: 'unset' }}
            h={'40px'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={'8px'}
            isLoading={saving}
            data-testid={'editar-modelo-submit'}
            rightIcon={isMobile ? <ReactSVG src={rightArrow} /> : undefined}
          >
            Salvar
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * Aviso de que o modelo em edição é institucional.
 *
 * Reaproveita a identidade visual da etiqueta "Institucional" dos cards, e explica o alcance
 * da edição — é a informação que faltava para o administrador saber que não está mexendo num
 * modelo só dele.
 */
function InstitutionalNotice() {
  return (
    <Flex
      mt={'12px'}
      px={'12px'}
      py={'8px'}
      gap={'8px'}
      alignItems={'center'}
      bg={'#F4FFF7'}
      borderRadius={'8px'}
      data-testid={'editar-modelo-institucional-aviso'}
    >
      <Box flexShrink={0} color={'#0E6928'} display={'flex'}>
        <HospitalBuildingPinIcon size={14} />
      </Box>
      <Text fontSize={'12px'} lineHeight={'1.3'} color={'#0E6928'}>
        Modelo institucional — as alterações valem para todos os prescritores do parceiro.
      </Text>
    </Flex>
  );
}

function Section(props: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Box px={'24px'} py={'8px'}>
        <Text fontSize={'14px'} fontWeight={600} color={'#1E252B'}>
          {props.title}
        </Text>
      </Box>
      {props.children}
    </Box>
  );
}

interface ItemRowProps {
  item: ItemPreview;
  onRemove: () => void;
  onUndoRemove: () => void;
  pendingRemoval: boolean;
}

function ItemRow(props: ItemRowProps) {
  const { item, onRemove, onUndoRemove, pendingRemoval } = props;
  return (
    <Flex
      alignItems={'flex-start'}
      gap={'12px'}
      px={'24px'}
      py={'12px'}
      borderTop={'1px solid #F1F3F5'}
      opacity={pendingRemoval ? 0.5 : 1}
      data-testid={`editar-modelo-item-${item.key}`}
      data-pending-removal={pendingRemoval ? 'true' : undefined}
    >
      <Flex direction={'column'} gap={'4px'} flex={1} minW={0}>
        <Text
          fontSize={'14px'}
          fontWeight={500}
          color={'#1E252B'}
          lineHeight={'1.3'}
          textDecoration={pendingRemoval ? 'line-through' : undefined}
        >
          {item.primary}
        </Text>
        {item.secondary && (
          <Text fontSize={'12px'} color={'#868E96'} lineHeight={'1.3'}>
            {item.secondary}
          </Text>
        )}
      </Flex>
      {pendingRemoval ? (
        <Button
          variant={'link'}
          onClick={onUndoRemove}
          color={'primary.main'}
          fontSize={'14px'}
          fontWeight={500}
          textDecoration={'underline'}
          minW={'auto'}
          h={'auto'}
          p={'4px'}
          flexShrink={0}
          _hover={{ textDecoration: 'underline' }}
          data-testid={`editar-modelo-undo-remove-${item.key}`}
        >
          Desfazer
        </Button>
      ) : (
        <IconButton
          aria-label={'Remover item'}
          icon={<BsTrash size={20} />}
          onClick={onRemove}
          variant={'ghost'}
          color={'#FF6161'}
          minW={'auto'}
          h={'auto'}
          p={'4px'}
          flexShrink={0}
          _hover={{ bg: 'transparent' }}
          _active={{ bg: 'transparent' }}
          data-testid={`editar-modelo-remove-${item.key}`}
        />
      )}
    </Flex>
  );
}
